import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Trophy, Users, CalendarDays, BarChart3, LayoutDashboard, User, Table2, Archive, UserCircle, Menu, X, Newspaper, ScrollText, Building2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';

const ADMIN_PASSWORD_KEY = 'tt-league:adminPassword:v1';
const ADMIN_NAME_KEY = 'tt-league:adminName:v1';

const Navbar = () => {
  const location = useLocation();
  const auth = useAuth();
  const toast = useToast();
  const role = auth.role;
  const [authOpen, setAuthOpen] = useState(false);
  const [authTab, setAuthTab] = useState('account');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [nameInput, setNameInput] = useState('');
  const [adminUsers, setAdminUsers] = useState([]);
  const [newAdminName, setNewAdminName] = useState('');
  const [newAdminPassword, setNewAdminPassword] = useState('');
  const [newAccountRole, setNewAccountRole] = useState('steward');
  const [resetTargetId, setResetTargetId] = useState(null);
  const [resetPasswordInput, setResetPasswordInput] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [confirmBackupRestore, setConfirmBackupRestore] = useState(false);
  const [restoreBackupFile, setRestoreBackupFile] = useState(null);
  const [confirmRestore, setConfirmRestore] = useState(false);
  const [premierConfirmText, setPremierConfirmText] = useState('');
  const overlayMouseDownRef = useRef(false);

  const getStoredAdminName = () => {
    try {
      return window.localStorage.getItem(ADMIN_NAME_KEY) || '';
    } catch (e) {
      return '';
    }
  };

  const fetchAdminUsers = async () => {
    try {
      const r = await axios.get('/api/admin/users');
      setAdminUsers(r.data || []);
    } catch (e) {
      // ignore
    }
  };

  useEffect(() => {
    if (authOpen && role === 'admin') {
      fetchAdminUsers();
      fetchMaintenance();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authOpen, role]);

  const addAccount = async () => {
    setAuthLoading(true);
    try {
      await axios.post('/api/admin/users', {
        name: newAdminName.trim(),
        password: newAdminPassword,
        role: newAccountRole,
      });
      setNewAdminName('');
      setNewAdminPassword('');
      setNewAccountRole('steward');
      await fetchAdminUsers();
      toast.success(`${newAccountRole === 'admin' ? 'Admin' : newAccountRole === 'captain' ? 'Team Captain' : 'Steward'} account created`);
    } catch (e) {
      toast.error(e?.response?.data?.error || e.message);
    } finally {
      setAuthLoading(false);
    }
  };

  const removeAdminUser = async (user) => {
    setAuthLoading(true);
    try {
      await axios.delete(`/api/admin/users/${user.id}`);
      setConfirmDeleteId(null);
      await fetchAdminUsers();
      toast.success('Account removed');
    } catch (e) {
      toast.error(e?.response?.data?.error || e.message);
    } finally {
      setAuthLoading(false);
    }
  };

  const resetAdminUserPassword = async (user) => {
    if (!resetPasswordInput.trim()) return;
    setAuthLoading(true);
    try {
      await axios.put(`/api/admin/users/${user.id}`, { password: resetPasswordInput.trim() });
      setResetTargetId(null);
      setResetPasswordInput('');
      toast.success('Password updated');
    } catch (e) {
      toast.error(e?.response?.data?.error || e.message);
    } finally {
      setAuthLoading(false);
    }
  };
  const [authLoading, setAuthLoading] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [backupLoading, setBackupLoading] = useState(false);
  const [backupRestoreLoading, setBackupRestoreLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [maintenance, setMaintenance] = useState({ enabled: false, message: '' });
  const [maintenanceLoading, setMaintenanceLoading] = useState(false);

  const fetchMaintenance = async () => {
    try {
      const r = await axios.get('/api/admin/maintenance');
      setMaintenance({ enabled: !!r.data?.enabled, message: r.data?.message || '' });
    } catch (e) {
      // ignore
    }
  };

  const toggleMaintenance = async () => {
    if (maintenanceLoading) return;
    setMaintenanceLoading(true);
    try {
      const r = await axios.put('/api/admin/maintenance', {
        enabled: !maintenance.enabled,
        message: maintenance.message,
      });
      setMaintenance({ enabled: !!r.data?.enabled, message: r.data?.message || '' });
      toast.success(r.data?.enabled ? 'Maintenance mode ON — visitors see the maintenance page' : 'Maintenance mode OFF — site is live');
    } catch (e) {
      toast.error(e?.response?.data?.error || e.message);
    } finally {
      setMaintenanceLoading(false);
    }
  };

  const refreshRole = async () => {
    const identity = await auth.refreshRole();
    if (identity.role === 'viewer') {
      try {
        if (window.localStorage.getItem(ADMIN_PASSWORD_KEY)) {
          window.localStorage.removeItem(ADMIN_PASSWORD_KEY);
        }
      } catch (e) {
        // ignore
      }
    }
  };

  const downloadDatabaseBackup = async () => {
    if (backupLoading) return;

    setBackupLoading(true);
    try {
      const response = await axios.get('/api/admin/database-backup', { responseType: 'blob' });
      const disposition = response.headers['content-disposition'] || '';
      const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
      const filename = filenameMatch?.[1] || `league-backup-${new Date().toISOString().slice(0, 10)}.db`;
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Database backup downloaded');
    } catch (e) {
      toast.error(e?.response?.data?.error || e.message);
    } finally {
      setBackupLoading(false);
    }
  };

  const restoreDatabaseBackup = async () => {
    if (backupRestoreLoading || !restoreBackupFile) return;

    setBackupRestoreLoading(true);
    try {
      const data = await restoreBackupFile.arrayBuffer();
      await axios.post('/api/admin/database-restore', data, {
        headers: { 'Content-Type': 'application/octet-stream' },
      });
      toast.success('Database backup restored');
      window.location.reload();
    } catch (e) {
      toast.error(e?.response?.data?.error || e.message);
    } finally {
      setBackupRestoreLoading(false);
      setConfirmBackupRestore(false);
      setRestoreBackupFile(null);
    }
  };

  const restorePremierSnapshot = async () => {
    if (restoreLoading) return;

    setRestoreLoading(true);
    try {
      await axios.post('/api/admin/restore-prem-snapshot', {});
      toast.success('Premier Division snapshot restored');
      window.location.reload();
    } catch (e) {
      toast.error(e?.response?.data?.error || e.message);
    } finally {
      setRestoreLoading(false);
      setConfirmRestore(false);
      setPremierConfirmText('');
    }
  };

  const changeAdminPassword = async () => {
    setAuthLoading(true);
    try {
      await axios.put('/api/auth/admin-password', { newPassword });
      window.localStorage.setItem(ADMIN_PASSWORD_KEY, newPassword);
      await refreshRole();
      setNewPassword('');
      setConfirmNewPassword('');
      toast.success('Password updated');
    } catch (e) {
      toast.error(e?.response?.data?.error || e.message);
    } finally {
      setAuthLoading(false);
    }
  };

  useEffect(() => {
    refreshRole();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!authOpen) return;
    const handler = (e) => {
      if (e.key === 'Escape') {
        closeAuth();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [authOpen]);

  const openAuth = () => {
    setAuthTab('account');
    setPasswordInput('');
    setNewPassword('');
    setConfirmNewPassword('');
    try {
      setNameInput(window.localStorage.getItem(ADMIN_NAME_KEY) || '');
    } catch (e) {
      setNameInput('');
    }
    setAuthOpen(true);
  };

  const closeAuth = () => {
    setAuthOpen(false);
    setPasswordInput('');
    setNewPassword('');
    setConfirmNewPassword('');
    setConfirmBackupRestore(false);
    setRestoreBackupFile(null);
    setConfirmRestore(false);
    setPremierConfirmText('');
  };

  const enableAdmin = async () => {
    setAuthLoading(true);
    try {
      const name = nameInput.trim();
      const r = await axios.post('/api/auth/login', { password: passwordInput, name });
      if (!['admin', 'steward', 'captain'].includes(r?.data?.role)) {
        toast.error('Incorrect name or password');
        return;
      }
      window.localStorage.setItem(ADMIN_PASSWORD_KEY, passwordInput);
      window.localStorage.setItem(ADMIN_NAME_KEY, r.data.name || name);
      await refreshRole();
      closeAuth();
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error('Incorrect name or password');
      } else {
        toast.error(e?.response?.data?.error || e.message);
      }
    } finally {
      setAuthLoading(false);
    }
  };

  const disableAdmin = async () => {
    setAuthLoading(true);
    try {
      let name = '';
      try {
        name = window.localStorage.getItem(ADMIN_NAME_KEY) || '';
      } catch (ignore) {
        // ignore
      }
      try {
        await axios.post('/api/auth/logout', { name });
      } catch (ignore) {
        // ignore
      }
      window.localStorage.removeItem(ADMIN_PASSWORD_KEY);
      window.localStorage.removeItem(ADMIN_NAME_KEY);
      await refreshRole();
      closeAuth();
    } catch (e) {
      toast.error(e?.response?.data?.error || e.message);
    } finally {
      setAuthLoading(false);
    }
  };

  const navItems = [
    { path: '/', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/news', label: 'Announcements', icon: Newspaper },
    { path: '/players', label: 'Players', icon: User },
    { path: '/teams', label: 'Teams', icon: Users },
    { path: '/clubs', label: 'Clubs', icon: Building2 },
    { path: '/fixtures', label: 'Season Fixtures', icon: CalendarDays },
    { path: '/team-standings', label: 'Standings', icon: Table2 },
    { path: '/cup', label: 'Cup', icon: Trophy },
    { path: '/player-rankings', label: 'Player Rankings', icon: BarChart3 },
    { path: '/seasons', label: 'Seasons', icon: Archive },
    { path: '/activity', label: 'Activity', icon: ScrollText, adminOnly: true },
  ];

  const visibleNavItems = navItems.filter((item) => !item.adminOnly || role === 'admin');

  return (
    <nav className="bg-white shadow-lg">
      <div className="container mx-auto px-4">
        <div className="py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 min-w-0">
              <button
                type="button"
                className="md:hidden p-2 rounded hover:bg-gray-100"
                onClick={() => setMobileOpen((v) => !v)}
                aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              >
                {mobileOpen ? <X size={22} /> : <Menu size={22} />}
              </button>
              <img
                src="/logo.png"
                alt=""
                className="h-10 md:h-12 w-auto object-contain"
              />
              <h1 className="ml-2 text-sm sm:text-base md:text-lg font-bold text-gray-800 truncate">
                <span className="hidden sm:inline">Bray & District Table Tennis League</span>
                <span className="sm:hidden">Bray TT League</span>
              </h1>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                className="flex items-center gap-2 px-2 py-1 rounded hover:bg-gray-100"
                onClick={openAuth}
                title={role !== 'viewer' ? `${role === 'admin' ? 'Admin' : role === 'steward' ? 'Steward' : 'Team Captain'} enabled (click to manage)` : 'Viewer mode (click to sign in)'}
              >
                <span className={`hidden sm:inline text-xs font-semibold ${role !== 'viewer' ? 'text-green-700' : 'text-gray-600'}`}>
                  {role === 'admin' ? 'Admin' : role === 'steward' ? 'Steward' : role === 'captain' ? 'Captain' : 'Viewer'}
                </span>
                <UserCircle size={22} className={role !== 'viewer' ? 'text-green-700' : 'text-gray-700'} />
              </button>
            </div>
          </div>

          <div className="mt-3 hidden md:flex items-center gap-2 flex-wrap">
            {visibleNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`nav-link flex items-center space-x-2 ${
                    isActive ? 'nav-link-active' : 'nav-link-inactive'
                  }`}
                >
                  <Icon size={18} />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </div>

          {mobileOpen && (
            <div className="md:hidden mt-3 border-t pt-3">
              <div className="grid grid-cols-1 gap-2">
                {visibleNavItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;

                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`nav-link flex items-center space-x-2 ${
                        isActive ? 'nav-link-active' : 'nav-link-inactive'
                      }`}
                    >
                      <Icon size={18} />
                      <span>{item.label}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {authOpen && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
          onMouseDown={(e) => {
            overlayMouseDownRef.current = e.target === e.currentTarget;
          }}
          onClick={() => {
            if (overlayMouseDownRef.current) closeAuth();
          }}
        >
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-semibold text-gray-800">Access</h2>
              <button type="button" className="p-2 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700" onClick={closeAuth}>
                ✕
              </button>
            </div>

            {role !== 'viewer' ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                  <div>
                    <div className="text-xs uppercase tracking-wide text-gray-500">Signed in as</div>
                    <div className="font-semibold text-gray-900">{getStoredAdminName() || role}</div>
                  </div>
                  <span className="rounded-full bg-green-100 px-3 py-1 text-xs font-semibold capitalize text-green-800">{role}</span>
                </div>

                <div className="grid grid-cols-3 rounded-lg bg-gray-100 p-1">
                  {[
                    ['account', 'Account'],
                    ...(role === 'admin' ? [['accounts', 'Accounts']] : []),
                    ['data', 'Data'],
                  ].map(([key, label]) => (
                    <button
                      key={key}
                      type="button"
                      className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${authTab === key ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
                      onClick={() => setAuthTab(key)}
                    >
                      {label}
                    </button>
                  ))}
                </div>

                {authTab === 'account' && (
                  <div className="space-y-3">
                    <div>
                      <div className="text-sm font-semibold text-gray-800">Change password</div>
                      <div className="text-sm text-gray-600">Update the password for your current account.</div>
                    </div>
                    <label className="block text-sm text-gray-700">
                      New password
                      <input
                        className="input mt-1"
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                      />
                    </label>
                    <label className="block text-sm text-gray-700">
                      Confirm new password
                      <input
                        className="input mt-1"
                        type="password"
                        value={confirmNewPassword}
                        onChange={(e) => setConfirmNewPassword(e.target.value)}
                      />
                    </label>
                    {confirmNewPassword && newPassword !== confirmNewPassword && (
                      <div className="text-sm text-red-600">Passwords do not match.</div>
                    )}
                    <div className="flex justify-end">
                      <button
                        className="btn btn-success"
                        type="button"
                        onClick={changeAdminPassword}
                        disabled={authLoading || !newPassword || newPassword !== confirmNewPassword}
                      >
                        Update password
                      </button>
                    </div>
                  </div>
                )}

                {role === 'admin' && authTab === 'accounts' && (
                  <div className="space-y-4">
                  <div>
                    <div className="text-sm font-semibold text-gray-800">Accounts</div>
                    <div className="text-sm text-gray-600">Manage admin, steward and team captain sign-in accounts.</div>
                  </div>
                  <div className="space-y-3">
                    {adminUsers.map((user) => (
                      <div key={user.id} className="rounded-lg border border-gray-200 p-3 text-sm">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900">{user.name}</span>
                            <span className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${user.role === 'admin' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-700'}`}>
                              {user.role}
                            </span>
                          </div>
                          {resetTargetId !== user.id && confirmDeleteId !== user.id && (
                            <div className="flex gap-2">
                              <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() => {
                                  setResetTargetId(user.id);
                                  setResetPasswordInput('');
                                }}
                                disabled={authLoading}
                              >
                                Reset password
                              </button>
                              <button
                                type="button"
                                className="btn btn-danger"
                                onClick={() => setConfirmDeleteId(user.id)}
                                disabled={authLoading || user.id === auth.actorId || user.name === getStoredAdminName()}
                              >
                                Remove
                              </button>
                            </div>
                          )}
                          {confirmDeleteId === user.id && (
                            <div className="flex gap-2 items-center">
                              <span className="text-xs text-gray-600">Remove account?</span>
                              <button
                                type="button"
                                className="btn btn-danger"
                                onClick={() => removeAdminUser(user)}
                                disabled={authLoading}
                              >
                                Confirm
                              </button>
                              <button
                                type="button"
                                className="btn"
                                onClick={() => setConfirmDeleteId(null)}
                                disabled={authLoading}
                              >
                                Cancel
                              </button>
                            </div>
                          )}
                        </div>
                        {resetTargetId === user.id && (
                          <div className="mt-2 flex gap-2">
                            <input
                              className="input flex-1"
                              type="password"
                              value={resetPasswordInput}
                              onChange={(e) => setResetPasswordInput(e.target.value)}
                              placeholder={`New password for ${user.name}`}
                              autoFocus
                            />
                            <button
                              type="button"
                              className="btn btn-primary"
                              onClick={() => resetAdminUserPassword(user)}
                              disabled={authLoading || resetPasswordInput.trim().length < 3}
                            >
                              Set
                            </button>
                            <button
                              type="button"
                              className="btn"
                              onClick={() => {
                                setResetTargetId(null);
                                setResetPasswordInput('');
                              }}
                              disabled={authLoading}
                            >
                              Cancel
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="border-t border-gray-200 pt-4 space-y-3">
                    <div className="text-sm font-semibold text-gray-800">Add account</div>
                    <label className="block text-sm text-gray-700">
                      Name
                      <input
                        className="input mt-1"
                        type="text"
                        value={newAdminName}
                        onChange={(e) => setNewAdminName(e.target.value)}
                      />
                    </label>
                    <label className="block text-sm text-gray-700">
                      Temporary password
                      <input
                        className="input mt-1"
                        type="password"
                        value={newAdminPassword}
                        onChange={(e) => setNewAdminPassword(e.target.value)}
                      />
                    </label>
                    <label className="block text-sm text-gray-700">
                      Role
                      <select
                        className="input mt-1"
                        value={newAccountRole}
                        onChange={(e) => setNewAccountRole(e.target.value)}
                      >
                        <option value="steward">Steward — editing only</option>
                        <option value="captain">Team Captain — viewing + contact details</option>
                        <option value="admin">Admin — full access incl. backups</option>
                      </select>
                    </label>
                    <div className="flex justify-end">
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={addAccount}
                        disabled={authLoading || !newAdminName.trim() || newAdminPassword.length < 3}
                      >
                        Create account
                      </button>
                    </div>
                  </div>
                </div>
                )}

                {authTab === 'data' && (
                  <div className="space-y-4">
                    {role === 'admin' && (
                      <div className={`rounded-lg border p-4 space-y-3 ${maintenance.enabled ? 'border-amber-300 bg-amber-50' : 'border-gray-200'}`}>
                        <div>
                          <div className="text-sm font-semibold text-gray-900">
                            Maintenance mode{maintenance.enabled ? ' — ON' : ''}
                          </div>
                          <div className="text-sm text-gray-600">
                            {maintenance.enabled
                              ? 'Visitors see the maintenance page; admins keep full access.'
                              : 'Take the site down for visitors while you work on it.'}
                          </div>
                        </div>
                        <input
                          className="input w-full"
                          type="text"
                          maxLength={200}
                          placeholder="Message shown on the maintenance page (optional)"
                          value={maintenance.message}
                          onChange={(e) => setMaintenance((m) => ({ ...m, message: e.target.value }))}
                          disabled={maintenanceLoading}
                        />
                        <div className="flex justify-end">
                          <button
                            className={maintenance.enabled ? 'btn btn-success' : 'btn btn-warning'}
                            type="button"
                            onClick={toggleMaintenance}
                            disabled={maintenanceLoading}
                          >
                            {maintenanceLoading ? 'Updating...' : maintenance.enabled ? 'Bring site back up' : 'Take site down'}
                          </button>
                        </div>
                      </div>
                    )}
                    <div className="rounded-lg border border-gray-200 p-4 space-y-3">
                      <div>
                        <div className="text-sm font-semibold text-gray-900">Download backup</div>
                        <div className="text-sm text-gray-600">Save a consistent copy of the current database.</div>
                      </div>
                      <div className="flex justify-end">
                        <button
                          className="btn btn-success"
                          type="button"
                          onClick={downloadDatabaseBackup}
                          disabled={authLoading || backupLoading || backupRestoreLoading || restoreLoading}
                        >
                          {backupLoading ? 'Preparing...' : 'Download backup'}
                        </button>
                      </div>
                    </div>

                    {role === 'admin' && (
                      <div className="rounded-lg border border-gray-200 p-4 space-y-3">
                        <div>
                          <div className="text-sm font-semibold text-gray-900">Restore from backup</div>
                          <div className="text-sm text-gray-600">Select a previously downloaded SQLite backup. Maximum size: 50 MB.</div>
                        </div>
                        <label
                          className="flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-center hover:border-blue-400 hover:bg-blue-50"
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={(e) => {
                            e.preventDefault();
                            setRestoreBackupFile(e.dataTransfer.files?.[0] || null);
                            setConfirmBackupRestore(false);
                          }}
                        >
                          <span className="text-sm font-medium text-gray-800">Select a .db backup</span>
                          <span className="mt-1 text-xs text-gray-500">or drop it here</span>
                          <input
                            key={restoreBackupFile ? restoreBackupFile.name : 'no-backup-selected'}
                            className="sr-only"
                            type="file"
                            accept=".db,application/octet-stream"
                            onChange={(e) => {
                              setRestoreBackupFile(e.target.files?.[0] || null);
                              setConfirmBackupRestore(false);
                            }}
                            disabled={backupLoading || backupRestoreLoading || restoreLoading}
                          />
                        </label>
                        {restoreBackupFile && (
                          <div className="rounded-md bg-gray-50 px-3 py-2">
                            <div className="truncate text-sm font-medium text-gray-900">{restoreBackupFile.name}</div>
                            <div className="text-xs text-gray-500">{(restoreBackupFile.size / 1024 / 1024).toFixed(2)} MB</div>
                          </div>
                        )}
                        {restoreBackupFile && (
                          <div className="flex flex-wrap justify-end gap-2">
                            {confirmBackupRestore ? (
                              <>
                                <span className="self-center text-xs font-medium text-red-700">Replace all current data?</span>
                                <button className="btn btn-danger" type="button" onClick={restoreDatabaseBackup} disabled={backupRestoreLoading}>
                                  {backupRestoreLoading ? 'Restoring...' : 'Confirm restore'}
                                </button>
                                <button className="btn btn-secondary" type="button" onClick={() => setConfirmBackupRestore(false)} disabled={backupRestoreLoading}>
                                  Cancel
                                </button>
                              </>
                            ) : (
                              <button className="btn btn-warning" type="button" onClick={() => setConfirmBackupRestore(true)}>
                                Restore selected backup
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {role === 'admin' && (
                      <div className="rounded-lg border border-red-200 bg-red-50 p-4 space-y-3">
                        <div>
                          <div className="text-sm font-semibold text-red-900">Danger zone</div>
                          <div className="mt-2 text-sm font-medium text-red-900">Restore Premier snapshot</div>
                          <div className="text-sm text-red-700">This replaces current league data with the bundled Premier snapshot. Download a backup first.</div>
                        </div>
                        {confirmRestore ? (
                          <div className="space-y-3">
                            <label className="block text-sm text-red-900">
                              Type RESTORE to continue
                              <input
                                className="input mt-1"
                                value={premierConfirmText}
                                onChange={(e) => setPremierConfirmText(e.target.value)}
                                autoFocus
                              />
                            </label>
                            <div className="flex justify-end gap-2">
                              <button
                                className="btn btn-danger"
                                type="button"
                                onClick={restorePremierSnapshot}
                                disabled={authLoading || restoreLoading || backupLoading || premierConfirmText !== 'RESTORE'}
                              >
                                {restoreLoading ? 'Restoring...' : 'Restore Premier snapshot'}
                              </button>
                              <button
                                className="btn btn-secondary"
                                type="button"
                                onClick={() => {
                                  setConfirmRestore(false);
                                  setPremierConfirmText('');
                                }}
                                disabled={authLoading || restoreLoading}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex justify-end">
                            <button
                              className="btn btn-danger"
                              type="button"
                              onClick={() => {
                                setConfirmRestore(true);
                                setPremierConfirmText('');
                              }}
                              disabled={authLoading || restoreLoading || backupLoading}
                            >
                              Restore Premier snapshot
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <div className="flex items-center justify-between border-t border-gray-200 pt-4">
                  <button className="btn btn-secondary" type="button" onClick={disableAdmin} disabled={authLoading}>
                    Sign out
                  </button>
                  <button className="btn btn-primary" type="button" onClick={closeAuth} disabled={authLoading}>
                    Done
                  </button>
                </div>
              </div>
            ) : (
              <form
                className="space-y-4"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!authLoading && passwordInput && nameInput.trim()) enableAdmin();
                }}
              >
                <div className="text-sm text-gray-700">Sign in with your admin, steward or team captain name and password. Admin and steward actions are recorded in the activity log.</div>
                <input
                  className="input w-full"
                  type="text"
                  value={nameInput}
                  onChange={(e) => setNameInput(e.target.value)}
                  placeholder="Account name"
                  autoFocus
                  autoComplete="username"
                />
                <input
                  className="input w-full"
                  type="password"
                  value={passwordInput}
                  onChange={(e) => setPasswordInput(e.target.value)}
                  placeholder="Password"
                  autoComplete="current-password"
                />
                <div className="flex justify-end gap-2">
                  <button className="btn" type="button" onClick={closeAuth} disabled={authLoading}>
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    type="submit"
                    disabled={authLoading || !passwordInput || !nameInput.trim()}
                  >
                    {authLoading ? 'Signing in...' : 'Sign In'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

    </nav>
  );
};

export default Navbar;
